import {
  PRODUCT_NEW_FORM_CONFIG_LOADED,
  PRODUCT_NEW_FORM_CONFIG_CLEAR,
  PRODUCT_NEW_FORM_BULK_CONFIG_LOADED,
  PRODUCT_NEW_FORM_BULK_CONFIG_CLEAR,
  PRODUCT_NEW_SEARCHES_TEAMS_LOADED,
  PRODUCT_NEW_SEARCHES_TEAMS_CLEAR,
} from '../../actionTypes';
import * as api from '../../apiHelper';
import { initiateRedirectRequest } from '../../redirectActions';
import {
  setFetchLoaderLoading,
  setFetchLoaderReset,
  setSubmissionLoaderLoading,
  setSubmissionLoaderReset,
} from '../../fetchLoaderActions';
import {
  arriveNotice,
  arriveCustomNotice,
} from '../../noticeActions';

// Reducer corresponding actions
function newFormConfigClear() {
  return { type: PRODUCT_NEW_FORM_CONFIG_CLEAR };
}

function newFormConfigLoaded(formConfig) {
  return {
    type: PRODUCT_NEW_FORM_CONFIG_LOADED,
    payload: { formConfig },
  };
}

function newBulkFormConfigClear() {
  return { type: PRODUCT_NEW_FORM_BULK_CONFIG_CLEAR };
}

function newBulkFormConfigLoaded(bulkFormConfig) {
  return {
    type: PRODUCT_NEW_FORM_BULK_CONFIG_LOADED,
    payload: { bulkFormConfig },
  };
}

function newSearchesTeamsLoaded(teams) {
  return {
    type: PRODUCT_NEW_SEARCHES_TEAMS_LOADED,
    payload: { teams },
  };
}

function newSearchesTeamsClear() {
  return { type: PRODUCT_NEW_SEARCHES_TEAMS_CLEAR };
}

// API
async function getNewFormConfigApi(args, token) {
  const response = await api.get({
    path: 'search-types',
    params: args,
    token,
    version: 'gateway',
  });
  return response.json();
}

async function getNewBulkFormConfigApi(args, token) {
  const response = await api.get({
    path: 'search-types/bulk',
    params: args,
    token,
    version: 'gateway',
  });
  return response.json();
}

async function getNewSearchesTeamsApi(token) {
  const response = await api.get({
    path: 'search_types/company_teams',
    token,
    version: 'v3',
  });
  return response.json();
}

async function addNewSearchesTeamsApi(body, token) {
  const response = await api.post({
    path: 'search_types/add_team',
    body,
    token,
    version: 'v3',
  });
  return response.json();
}

async function deleteNewSearchesTeamsApi(body, token) {
  const response = await api.post({
    path: 'search_types/delete_team',
    body,
    token,
    version: 'v3',
  });
  return response.json();
}

async function updateNewSearchesTeamsApi(body, token) {
  const response = await api.post({
    path: 'search_types/edit_teams',
    body,
    token,
    version: 'v3',
  });
  return response.json();
}

async function submitNewSearchApi(body, token, dispatch) {
  // Use API Gateway for search creation (JSON format)
  const response = await api.post({
    path: 'searches',
    body,
    token,
    version: 'gateway',
    customError: true,
  });

  if (!response.ok) {
    const json = await response.json();

    if (json?.errors) {
      const customError = api.extractErrorMessage(json.errors);
      dispatch(arriveCustomNotice(customError));

      throw customError;
    } else {
      const formattedMessage = api.formatJsonError(json.error || json.message);
      const error = new Error(formattedMessage);
      dispatch(arriveNotice(error.message));

      throw error;
    }
  }

  return response.json();
}

async function submitNewBulkSearchApi(body, token, dispatch) {
  // Use API Gateway for bulk search creation
  const response = await api.mixedFormDataRequest({
    path: 'searches/bulk',
    body,
    token,
    version: 'gateway',
    customError: true,
  });
  const json = await response.json();

  // Handle partial success (some searches created despite errors)
  // Search service returns 400 even with partial success
  if (json.created_count > 0) {
    // Some searches were successfully created
    let message = json.message || 'Bulk searches created';

    // Add error details if some rows failed
    if (json.error_count && Array.isArray(json.errors) && json.errors.length) {
      // Show first few errors to avoid overwhelming the user
      const errorPreview = json.errors.slice(0, 5).join('\n');
      const remainingErrors = json.errors.length > 5 ? `\n...and ${json.errors.length - 5} more errors` : '';
      message = `${message}\n\nFailed rows:\n${errorPreview}${remainingErrors}`;
    }

    // Show as warning if there were errors, success if all succeeded
    const noticeType = json.error_count > 0 ? 'warning' : 'ok';
    dispatch(arriveNotice(message, noticeType));

    return json;
  }

  // Complete failure or error response
  if (!response.ok) {
    if (json?.errors) {
      const customError = api.extractErrorMessage(json.errors);
      dispatch(arriveCustomNotice(customError));

      throw customError;
    } else {
      const formattedMessage = api.formatJsonError(json.error || json.message);
      const error = new Error(formattedMessage);
      dispatch(arriveNotice(error.message));

      throw error;
    }
  }

  return json;
}

async function submitSearchFeatureApi(id, feature, body, token) {
  // Use API Gateway for pause/resume operations
  if (feature === 'pause' || feature === 'resume') {
    const useBody = {
      searchIds: [id],
      pause: feature === 'pause',
    };

    const response = await api.post({
      path: 'searches/pause-resume',
      body: useBody,
      token,
      version: 'gateway',
      customError: true,
    });

    if (!response.ok) {
      const errorData = await response.json();
      const formattedMessage = api.formatJsonError(errorData.message || errorData.error);
      const error = new Error(formattedMessage);
      throw error;
    }

    const data = await response.json();
    // Map 'message' field to 'notice' for consistency with existing code
    return {
      notice: data.message,
    };
  }

  // Use API Gateway for prioritize/deprioritize operations
  if (feature === 'prioritize' || feature === 'deprioritize') {
    const useBody = {
      searchIds: [id],
      prioritize: feature === 'prioritize',
    };

    const response = await api.post({
      path: 'searches/prioritization',
      body: useBody,
      token,
      version: 'gateway',
      customError: true,
    });

    if (!response.ok) {
      const errorData = await response.json();
      const formattedMessage = api.formatJsonError(errorData.message || errorData.error);
      const error = new Error(formattedMessage);
      throw error;
    }

    const data = await response.json();
    // Map 'message' field to 'notice' for consistency with existing code
    return {
      notice: data.message,
    };
  }

  // Use Rails for other features (fallback to existing behavior)
  const response = await api.post({
    path: `searches/${id}/${feature}`,
    body,
    token,
    version: 'v3',
  });
  return response.json();
}

// Public components actions
export function clearNewFormConfigRequest() {
  return (dispatch) => {
    dispatch(newFormConfigClear());
  };
}

export function initiateNewFormConfigRequest(args) {
  return async (dispatch, getState) => {
    try {
      dispatch(newFormConfigClear());

      const { auth: { token }, fetchLoader: { dataLoading } } = getState();
      if (!dataLoading) dispatch(setFetchLoaderLoading());

      const response = await getNewFormConfigApi(args, token);
      dispatch(newFormConfigLoaded(response));
      dispatch(setFetchLoaderReset());
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setFetchLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function clearNewBulkFormConfigRequest() {
  return (dispatch) => {
    dispatch(newBulkFormConfigClear());
  };
}

export function initiateNewBulkFormConfigRequest(args) {
  return async (dispatch, getState) => {
    try {
      dispatch(newBulkFormConfigClear());

      const { auth: { token }, fetchLoader: { dataLoading } } = getState();
      if (!dataLoading) dispatch(setFetchLoaderLoading());

      const response = await getNewBulkFormConfigApi(args, token);
      dispatch(newBulkFormConfigLoaded(response));
      dispatch(setFetchLoaderReset());
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setFetchLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitNewSearchRequest(query, rootPath, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { auth: { token }, fetchLoader: { submissionLoading } } = getState();
      if (!submissionLoading) dispatch(setSubmissionLoaderLoading());

      const { notice } = await submitNewSearchApi(query, token, dispatch);

      dispatch(arriveNotice(notice, 'ok'));
      resetForm();
      dispatch(initiateRedirectRequest(null, rootPath, true));
      dispatch(setSubmissionLoaderReset());
    } catch (e) {
      dispatch(setSubmissionLoaderReset());
    }
  };
}

export function submitNewBulkSearchRequest(query, rootPath, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { auth: { token }, fetchLoader: { submissionLoading } } = getState();
      if (!submissionLoading) dispatch(setSubmissionLoaderLoading());

      const { notice } = await submitNewBulkSearchApi(query, token, dispatch);

      dispatch(arriveNotice(notice, 'ok'));
      resetForm();
      dispatch(initiateRedirectRequest(null, rootPath, true));
      dispatch(setSubmissionLoaderReset());
    } catch (e) {
      dispatch(setSubmissionLoaderReset());
    }
  };
}

export function submitSearchFeatureRequest(id, feature, redirectPath, body) {
  return async (dispatch, getState) => {
    try {
      const { token } = getState().auth;
      const { notice } = await submitSearchFeatureApi(id, feature, body, token);

      dispatch(arriveNotice(notice, 'ok'));
      dispatch(initiateRedirectRequest(null, redirectPath, true));
    } catch (e) {
      dispatch(arriveNotice(e.message));
    }
  };
}

export function clearNewSearchesTeamsRequest() {
  return (dispatch) => {
    dispatch(newSearchesTeamsClear());
  };
}

export function initiateNewSearchesTeamsRequest() {
  return async (dispatch, getState) => {
    try {
      dispatch(newSearchesTeamsClear());
      const { auth: { token }, fetchLoader: { dataLoading } } = getState();
      if (!dataLoading) dispatch(setFetchLoaderLoading());

      const response = await getNewSearchesTeamsApi(token);
      dispatch(newSearchesTeamsLoaded(response));
      dispatch(setFetchLoaderReset());
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
        dispatch(setFetchLoaderReset());
      }
    }
  };
}

export function updateNewSearchesTeamsRequest(body, actionType) {
  return async (dispatch, getState) => {
    try {
      const { auth: { token }, fetchLoader: { submissionLoading } } = getState();
      if (!submissionLoading) dispatch(setSubmissionLoaderLoading());

      let response;
      switch (actionType) {
        case 'push':
          response = await addNewSearchesTeamsApi(body, token);
          break;
        case 'delete':
          response = await deleteNewSearchesTeamsApi(body, token);
          break;
        case 'edit':
          response = await updateNewSearchesTeamsApi(body, token);
          break;
        default:
          throw new Error('Invalid action type for updating search teams');
      }

      dispatch(newSearchesTeamsLoaded(response));
      dispatch(arriveNotice('Teams updated!', 'ok'));
      dispatch(setSubmissionLoaderReset());
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
        dispatch(setSubmissionLoaderReset());
      }
    }
  };
}
